<template>
  <div class="relative">
    <!-- پیام محدودیت -->
    <div
        v-if="userStatus === 'restricted'"
        class="bg-yellow-100 text-yellow-800 text-center py-2 font-medium">
      Your account is restricted. Reason: {{ restrictReason }}
    </div>

    <!-- SIDEBAR -->
    <div
        :class="[isOpen ? 'w-56' : 'w-16']"
        class="fixed left-0 top-0 h-full bg-primary text-white flex flex-col pt-40 pb-4 transition-all duration-300 rounded-r-xl z-50"
        @mouseenter="openMainMenu"
        @mouseleave="startCloseTimer"
    >
      <div class="flex flex-col gap-2 mt-6 px-2 overflow-y-auto">
        <template v-for="(item, index) in menuItems" :key="index">

          <!-- Logout همیشه فعال -->
          <div v-if="item.action === 'logout'"
               @click="handleLogout"
               class="flex items-center gap-3 px-3 py-2 rounded-md transition hover:bg-white hover:text-primary cursor-pointer">
            <component :is="item.icon" class="w-5 h-5"/>
            <span v-if="isOpen" class="whitespace-nowrap">{{ item.label }}</span>
          </div>

          <!-- سایر آیتم‌ها -->
          <div v-else>
            <div
                :class="isMenuDisabled
                ? 'bg-gray-700 opacity-70 text-gray-300 cursor-not-allowed'
                : 'hover:bg-white hover:text-primary cursor-pointer'"
                class="flex items-center gap-3 px-3 py-2 rounded-md transition"
                @mouseenter="handleMenuHover(item, index)"
                @click="!isMenuDisabled && !item.submenu && item.route ? router.push(item.route) : null"
            >
              <component :is="item.icon" class="w-5 h-5"/>
              <span v-if="isOpen" class="whitespace-nowrap">{{ item.label }}</span>
            </div>
          </div>

        </template>
      </div>

      <!-- SUBMENU -->
      <transition name="submenu-fade">
        <div
            v-if="activeSubmenu !== null && menuItems[activeSubmenu].submenu && !isMenuDisabled"
            class="fixed left-56 top-0 h-full w-72 bg-white text-gray-800 rounded-r-lg shadow-2xl p-4 z-[9999] overflow-y-auto"
            @mouseenter="cancelCloseTimer"
            @mouseleave="startCloseTimer"
        >
          <div class="text-lg text-center font-semibold text-gray-500 mt-20 mb-4 uppercase">
            {{ menuItems[activeSubmenu].label }}
          </div>

          <div class="flex flex-col gap-2">
            <div
                v-for="(sub, si) in menuItems[activeSubmenu].submenu"
                :key="si"
                :class="activeSubmenuItem === si && activeParent === activeSubmenu
                ? 'bg-orange-50 text-primary'
                : 'hover:bg-orange-50 hover:text-orange-600'"
                class="flex items-center gap-3 rounded-md cursor-pointer transition p-2"
                @click="goToPage(sub.route, si)"
            >
              <div class="w-12 h-12 flex justify-center items-center rounded-md border"
                   :class="activeSubmenuItem === si && activeParent === activeSubmenu ? 'border-primary' : 'border-gray-400'">
                <component :is="sub.icon" class="w-6 h-6"/>
              </div>
              <span class="text-sm font-medium">{{ sub.label }}</span>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <!-- MAIN CONTENT -->
    <div
        class="bg-gray-100 min-h-screen ml-16 transition-all duration-300 overflow-hidden"
        :class="{ 'ml-56': isOpen }"
    >
      <div class="max-w-screen-2xl mx-auto">
        <app-header />
        <main class="flex-1 overflow-y-auto">
          <router-view />
        </main>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import AppHeader from "@/components/AppHeader"
import {
  ArrowRightLeft, BadgeDollarSign, CreditCard, Home, LogOut, ReceiptText,
  RefreshCw, Settings, SquareChartGantt, Landmark, ClipboardClock,
  PiggyBank, Lock, History, SquarePen, Star, List, Bell
} from 'lucide-vue-next'

const router = useRouter()
const isOpen = ref(false)
const activeSubmenu = ref(null)
const activeParent = ref(null)
const activeSubmenuItem = ref(null)
const userStatus = ref(null)
const restrictReason = ref(null)
const forceChange = ref(false)

const closeTimer = ref(null)

const isMenuDisabled = computed(() => {
  return forceChange.value || userStatus.value === 'restricted'
})

onMounted(() => {
  userStatus.value = localStorage.getItem('user_status')
  restrictReason.value = localStorage.getItem('restrict_reason')
  forceChange.value = localStorage.getItem('force_password_change') === '1'
})

function openMainMenu() { isOpen.value = true }
function closeMainMenu() { isOpen.value = false }

function openSubmenu(index) {
  cancelCloseTimer()
  if(!isMenuDisabled.value) activeSubmenu.value = index
}

function startCloseTimer() {
  cancelCloseTimer()
  closeTimer.value = setTimeout(() => {
    activeSubmenu.value = null
    isOpen.value = false
  }, 300) // ← کمی تاخیر برای اجازه حرکت موس بین منو و زیرمنو
}

function cancelCloseTimer() {
  if (closeTimer.value) {
    clearTimeout(closeTimer.value)
    closeTimer.value = null
  }
}

function goToPage(route, si) {
  if (!route || isMenuDisabled.value) return;
  activeParent.value = activeSubmenu.value;
  activeSubmenuItem.value = si;
  const tab = menuItems[activeSubmenu.value].submenu[si].defaultTab || 'Movements';
  router.push({ path: route, query: { defaultTab: tab } });
}

function handleLogout() {
  localStorage.removeItem('token')
  localStorage.removeItem('force_password_change')
  closeMainMenu()
  activeSubmenu.value = null
  router.push('/login')
}

const menuItems = [
  { label: 'Bison Mobile®', icon: Home, route: '/' },
  { label: 'Money Transfer', icon: ArrowRightLeft, route: '/transfer'},
  {
    label: 'Pay', icon: CreditCard, route: '/pay', submenu: [
      { label: 'Services', route: '/pay/services', icon: ReceiptText },
      { label: 'State', route: '/pay/state', icon: Landmark },
    ]
  },
  { label: 'Currency Exchange', icon: RefreshCw, route: '/currency' },
  {
    label: 'For You', icon: BadgeDollarSign, route: '/operations', submenu: [
      { label: 'Pending Operations', route: '/operations', icon: ClipboardClock },
    ]
  },
  {
    label: 'View', icon: SquareChartGantt, route: '/view', submenu: [
      { label: 'Balance', route: '/view/balance', icon: PiggyBank, defaultTab: 'Balance' },
      { label: 'Transactions', route: '/view/balance', icon: List, defaultTab: 'Movements' },
      { label: 'NIB/IBAN', route: '/view/balance', icon: CreditCard, defaultTab: 'Details' },
    ]
  },
  {
    label: 'Personal Setup', icon: Settings, route: '/setting', submenu: [
      { label: 'Change Password', route: '/setting/change-password', icon: Lock },
      { label: 'Activity Log', route: '/setting/activity-log', icon: History },
      { label: 'Custom Account', route: '/setting/custom-account', icon: SquarePen },
      { label: 'Frequent Beneficiaries', route: '/setting/beneficiaries', icon: Star },
      { label: 'Notification Settings', route: '/setting/notifications', icon: Bell },
    ]
  },
  { label: 'Logout', icon: LogOut, action: 'logout' },
]


function handleMenuHover(item, index) {
  if (isMenuDisabled.value) return

  if (item.submenu) {
    openSubmenu(index)
  } else {
    activeSubmenu.value = null // 👈 این خط مهمه
  }
}
</script>

<style scoped>
.submenu-fade-enter-active,
.submenu-fade-leave-active {
  transition: all 0.16s ease;
}
.submenu-fade-enter-from,
.submenu-fade-leave-to {
  opacity: 0;
  transform: translateX(-6px);
}

html, body {
  overflow: hidden;
}
</style>

